// src/pages/ja/base64Page_ja.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../base64Page.css";

function Base64Page() {
  const [darkMode, setDarkMode] = useState(false);

  // CSR 기준 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Base64 エンコーダー/デコーダー",
      description:
        "テキストをBase64にエンコードしたり、Base64をUTF-8テキストにデコードできるTooLovesツール",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "Base64, Base64 Encoder, Base64 Decoder, ベース64, エンコード, デコード, テキスト変換, URLセーフ, TooLoves, 無料",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "JPY",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // ダーク/ライトモードに合わせて meta theme-color 同期
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // 隠しファイル入力の参照
  const fileInputRef = useRef(null);

  // 入力参照
  const inputRef = useRef(null);

  // 状態
  const [mode, setMode] = useState("encode"); // encode | decode
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [decodedBytes, setDecodedBytes] = useState(null); // decode 結果保持
  const [urlSafe, setUrlSafe] = useState(false);
  const [error, setError] = useState("");

  // ファイルボタン -> 隠し input をクリック
  const onPickClick = () => {
    if (!fileInputRef.current) return;
    try {
      fileInputRef.current.value = "";
    } catch {}
    fileInputRef.current.click();
  };

  // ファイル選択でテキストを読み込み
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setInputText(text);
    } catch (err) {
      setError("ファイルの読み込み中にエラーが発生しました。");
    }
  };

  // textarea 自動高さ調整
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoResize(inputRef.current);
  }, [inputText]);

  // Base64 ユーティリティ
  const bytesToBase64 = (bytes) => {
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      const sub = bytes.subarray(i, i + chunk);
      binary += String.fromCharCode(...sub);
    }
    return btoa(binary);
  };

  const base64ToBytes = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const encodeUtf8ToBase64 = (text, { urlSafe }) => {
    const bytes = new TextEncoder().encode(text);
    let b64 = bytesToBase64(bytes);
    if (urlSafe) {
      b64 = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    }
    return b64;
  };

  const normalizeBase64Input = (raw, { urlSafe }) => {
    let s = raw || "";
    // 空白除去
    s = s.replace(/\s+/g, "");
    if (urlSafe) s = s.replace(/-/g, "+").replace(/_/g, "/");
    // padding
    const pad = s.length % 4;
    if (pad > 0) s = s + "=".repeat(4 - pad);
    return s;
  };

  const decodeBase64ToUtf8 = (raw, { urlSafe }) => {
    const normalized = normalizeBase64Input(raw, { urlSafe });
    const bytes = base64ToBytes(normalized);
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return { text, bytes };
  };

  // 可読性フォーマッタ: 76文字改行 + インデント（エンコード時のみ）
  const wrapAndIndent = (str, width = 76, indent = "  ") => {
    if (!str) return "";
    const out = [];
    for (let i = 0; i < str.length; i += width) {
      out.push(indent + str.slice(i, i + width));
    }
    return out.join("\n");
  };

  const getDisplayOutput = () => {
    if (!outputText) return "";
    // Base64 エンコード結果のみ 76文字改行+インデント適用（デコードテキストは元のまま）
    if (mode === "encode") {
      return wrapAndIndent(outputText, 76, "  ");
    }
    return outputText;
  };

  // 変換処理
  const transform = () => {
    setError("");
    try {
      if (mode === "encode") {
        const b64 = encodeUtf8ToBase64(inputText, { urlSafe });
        setOutputText(b64);
        setDecodedBytes(null);
      } else {
        const { text, bytes } = decodeBase64ToUtf8(inputText, { urlSafe });
        setOutputText(text);
        setDecodedBytes(bytes);
      }
    } catch (err) {
      setOutputText("");
      setDecodedBytes(null);
      setError("無効な入力、または変換中にエラーが発生しました。");
    }
  };

  useEffect(() => {
    transform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText, mode, urlSafe]);

  // クリップボードにコピー（表示用フォーマット基準）
  const handleCopy = async () => {
    try {
      const data = getDisplayOutput() || "";
      await navigator.clipboard.writeText(data);
    } catch (err) {
      setError("クリップボードのコピーに失敗しました。");
    }
  };

  // ダウンロード: 両モードともTXTで保存（表示用フォーマット基準）
  const handleDownload = () => {
    try {
      const data = getDisplayOutput() || "";
      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      const filename = mode === "encode" ? "base64.txt" : "decoded.txt";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("ダウンロード中にエラーが発生しました。");
    }
  };

  const handleReset = () => {
    setInputText("");
    setOutputText("");
    setDecodedBytes(null);
    setMode("encode");
    setUrlSafe(false);
    setError("");
    requestAnimationFrame(() => {
      autoResize(inputRef.current);
    });
  };

  const handleSwap = () => {
    if (!outputText) return;
    if (mode === "encode") {
      setInputText(outputText);
      setMode("decode");
    } else {
      setInputText(outputText);
      setMode("encode");
    }
  };

  const isOutputEmpty = !outputText || outputText.trim() === "";
  const displayOutput = getDisplayOutput();

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ja" }}>
        <title>Base64 エンコーダー/デコーダー | TooLoves</title>
        <meta
          name="description"
          content="テキストをBase64にエンコードしたり、Base64文字列をUTF-8テキストにデコードできるオンラインツール"
        />
        <meta
          name="keywords"
          content="Base64, エンコード, デコード, URLセーフ, テキスト変換, TooLoves, 無料"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 初期 theme-color。ランタイムで useEffect により同期 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="Base64 エンコーダー/デコーダー | TooLoves" />
        <meta
          property="og:description"
          content="テキスト/base64 間の変換を簡単に実行"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-base64.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Base64 エンコーダー/デコーダー | TooLoves" />
        <meta
          name="twitter:description"
          content="テキスト/base64 間の変換を簡単に実行"
        />
        <meta name="twitter:image" content="/og-base64.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 上部アプリバー */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/ja" className="brand-link" aria-label="TooLoves ホームへ移動">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "ライトモード" : "ダークモード"}
            </button>
          </div>
        </div>
      </header>

      {/* 本文 */}
      <main
        className="container"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <section className="generator-card">
          <h2 className="card-title">Base64 エンコーダー/デコーダー</h2>
          <p className="card-subtitle">
            テキストをBase64にエンコードしたり、Base64をUTF-8テキストにデコードできます。
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* ファイルアップロード（隠し input + ボタン） */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="b64-file-input"
                type="file"
                accept="text/plain,.txt"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="テキストファイルを選択"
                className="btn btn-primary"
              >
                ファイルを選択
              </button>
            </div>

            {/* 入力 */}
            <div className="input-row" style={{ marginTop: 8 }}>
              <div className="io-wrap" style={{ width: "100%" }}>
                <textarea
                  ref={inputRef}
                  className="text-input text-inputtextarea mono"
                  placeholder={
                    mode === "encode"
                      ? "ここにテキストを入力するか、ファイルを選択してください。"
                      : "ここにBase64文字列を貼り付けてください。"
                  }
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    display: "block",
                    maxWidth: "1200px",
                    margin: "0 auto",
                  }}
                  spellCheck={false}
                />
              </div>
            </div>

            {/* オプション */}
            <div className="options-row" style={{ gap: 16 }}>
              <div className="field">
                <label className="form-label">モード</label>
                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="encode">エンコード（テキスト → Base64）</option>
                  <option value="decode">デコード（Base64 → テキスト）</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">URL-safe</label>
                <input
                  type="checkbox"
                  checked={urlSafe}
                  onChange={(e) => setUrlSafe(e.target.checked)}
                />
              </div>
            </div>
          </form>

          {/* 出力 */}
          <div className="metadata-section" style={{ paddingTop: 8 }}>
            <div className="block">
              <div className="label-row">
                <label htmlFor="b64-output" className="label-strong">
                  出力結果
                </label>
                {/* 上部の label-actions（コピー/ダウンロード）は削除済み */}
              </div>

              <div
                id="b64-output"
                role="textbox"
                aria-readonly="true"
                tabIndex={0}
                className="mono-box"
                style={{ whiteSpace: "pre-wrap" }} // 改行/インデント保持
              >
                {displayOutput ||
                  (mode === "encode"
                    ? "エンコード結果がここに表示されます。"
                    : "デコード結果がここに表示されます。")}
              </div>

              {error && (
                <p className="hint" role="alert" style={{ color: "#ef4444", marginTop: 8 }}>
                  {error}
                </p>
              )}
            </div>

            {/* 下部アクション: コピー/ダウンロード + 入れ替え/リセット */}
            <div className="actions">
              <button
                className="btn-primary"
                onClick={handleCopy}
                type="button"
                aria-label="出力結果をコピー"
                disabled={isOutputEmpty}
              >
                コピー
              </button>
              <button
                className="btn-primary"
                onClick={handleDownload}
                type="button"
                aria-label="出力結果のtxtをダウンロード"
                disabled={isOutputEmpty}
              >
                txt ダウンロード
              </button>
              <button className="btn-secondary" onClick={handleSwap} type="button">
                入れ替え
              </button>
              <button className="btn-secondary" onClick={handleReset} type="button">
                リセット
              </button>
            </div>

            {/* デコード時のバイト長ヒント */}
            {mode === "decode" && decodedBytes && (
              <p className="hint" style={{ marginTop: 8 }}>
                バイト長: {decodedBytes.byteLength.toLocaleString()} バイト
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法的通知リンク">
          <Link to="/ja/terms" className="footer-link">
            利用規約
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/ja/privacy" className="footer-link">
            プライバシーポリシー
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default Base64Page;